import { useState, useEffect, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { columns } from './kanbanTaskConstants';
import useClinicTasks from './useClinicTasks';
import useRecurrentTasks from './useRecurrentTasks';
import { generateTodayTasks } from './generateTodayTasks';

const sortColumnTasks = (a, b) => {
  const dateA = a.executionDate
    ? a.executionDate.startOf('day').valueOf()
    : Number.MAX_SAFE_INTEGER;
  const dateB = b.executionDate
    ? b.executionDate.startOf('day').valueOf()
    : Number.MAX_SAFE_INTEGER;
  if (dateA !== dateB) return dateA - dateB;
  const sequenceA = a.sequence !== undefined ? a.sequence : Number.MAX_SAFE_INTEGER;
  const sequenceB = b.sequence !== undefined ? b.sequence : Number.MAX_SAFE_INTEGER;
  if (sequenceA !== sequenceB) return sequenceA - sequenceB;
  return a.id - b.id;
};

// Orquestra o quadro de tarefas (dia selecionado, modais e ações). Consumido
// pelas versões web e mobile, que ficam responsáveis apenas pela apresentação.
export default function useTaskBoard() {
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const [recurringModalOpen, setRecurringModalOpen] = useState(false);

  const [deleteModal, setDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesTaskId, setNotesTaskId] = useState(null);

  const {
    tasks,
    isLoading,
    showSuccessMessage,
    setShowSuccessMessage,
    successMessage,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
    fetchTasks,
    addTask,
    updateTask,
    changeTaskStatus,
    deleteTask,
    addAnnotation,
  } = useClinicTasks();

  const {
    recurrentTasks: recurringTasks,
    isLoading: isRecurringLoading,
    showSuccessMessage: showRecurringSuccessMessage,
    setShowSuccessMessage: setShowRecurringSuccessMessage,
    successMessage: recurringSuccessMessage,
    showErrorMessage: showRecurringErrorMessage,
    setShowErrorMessage: setShowRecurringErrorMessage,
    errorMessage: recurringErrorMessage,
    fetchRecurrentTasks,
    addRecurrentTask,
    updateRecurrentTask,
    changeRecurrentTaskOrder,
    deleteRecurrentTask,
  } = useRecurrentTasks();

  useEffect(() => {
    // Gerar as tarefas do dia pode demorar, então não seguramos a tela esperando: listamos o que
    // já existe e, quando a geração termina, relistamos em silêncio para incorporar o que ela criou.
    fetchTasks();
    fetchRecurrentTasks();
    generateTodayTasks().then(() => fetchTasks(false));
  }, [fetchTasks, fetchRecurrentTasks]);

  // Both hooks keep their own feedback state, but the page shows a single overlay and snackbar
  const closeSuccessMessage = useCallback(
    (value) => {
      setShowSuccessMessage(value);
      setShowRecurringSuccessMessage(value);
    },
    [setShowSuccessMessage, setShowRecurringSuccessMessage],
  );

  const closeErrorMessage = useCallback(
    (value) => {
      setShowErrorMessage(value);
      setShowRecurringErrorMessage(value);
    },
    [setShowErrorMessage, setShowRecurringErrorMessage],
  );

  const goToPreviousDay = useCallback(
    () => setSelectedDate((previousDate) => previousDate.subtract(1, 'day')),
    [],
  );
  const goToNextDay = useCallback(
    () => setSelectedDate((previousDate) => previousDate.add(1, 'day')),
    [],
  );
  const goToToday = useCallback(() => setSelectedDate(dayjs()), []);

  const openNewTaskModal = useCallback(() => {
    setEditingTask(null);
    setTaskModalOpen(true);
  }, []);

  const openEditTaskModal = useCallback((task) => {
    setEditingTask(task);
    setTaskModalOpen(true);
  }, []);

  const closeTaskModal = useCallback(() => {
    setTaskModalOpen(false);
    setEditingTask(null);
  }, []);

  const handleSaveTask = useCallback(
    (taskForm) => {
      const request =
        editingTask !== null ? updateTask(editingTask.id, taskForm) : addTask(taskForm);
      request.then((saved) => {
        if (!saved) return;
        closeTaskModal();
        fetchTasks();
      });
    },
    [editingTask, updateTask, addTask, closeTaskModal, fetchTasks],
  );

  const handleRecurringAdd = useCallback(
    (recurringForm) =>
      addRecurrentTask(recurringForm, recurringTasks.length).then((added) => {
        if (added) fetchRecurrentTasks();
        return added;
      }),
    [addRecurrentTask, recurringTasks.length, fetchRecurrentTasks],
  );

  const handleRecurringUpdate = useCallback(
    (id, recurringForm) => {
      const current = recurringTasks.find((recurringTask) => recurringTask.id === id);
      return updateRecurrentTask(id, recurringForm, current.order).then((updated) => {
        if (updated) fetchRecurrentTasks();
        return updated;
      });
    },
    [recurringTasks, updateRecurrentTask, fetchRecurrentTasks],
  );

  const handleRecurringDelete = useCallback(
    (id) =>
      deleteRecurrentTask(id).then((deleted) => {
        if (deleted) fetchRecurrentTasks();
        return deleted;
      }),
    [deleteRecurrentTask, fetchRecurrentTasks],
  );

  // The backend shifts the neighbours; the front only sends the moved task's new order
  const handleRecurringMove = useCallback(
    (index, targetIndex) => {
      const moved = recurringTasks[index];
      const target = recurringTasks[targetIndex];
      changeRecurrentTaskOrder(moved.id, target.order).then((changed) => {
        if (changed) fetchRecurrentTasks();
      });
    },
    [recurringTasks, changeRecurrentTaskOrder, fetchRecurrentTasks],
  );

  const handleChangeStage = useCallback(
    (task, direction) => {
      const currentIndex = columns.findIndex((column) => column.id === task.columnId);
      if (currentIndex === -1) return;
      const targetIndex = currentIndex + direction;
      if (targetIndex < 0 || targetIndex >= columns.length) return;
      changeTaskStatus(task.id, columns[targetIndex].id).then((changed) => {
        if (changed) fetchTasks();
      });
    },
    [changeTaskStatus, fetchTasks],
  );

  const handleAdvanceStage = useCallback((task) => handleChangeStage(task, 1), [handleChangeStage]);

  const handlePreviousStage = useCallback(
    (task) => handleChangeStage(task, -1),
    [handleChangeStage],
  );

  const handleDeleteClick = useCallback((id) => {
    setTaskToDelete(id);
    setDeleteModal(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModal(false);
    setTaskToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    deleteTask(taskToDelete).then((deleted) => {
      if (!deleted) return;
      setDeleteModal(false);
      setTaskToDelete(null);
      fetchTasks();
    });
  }, [deleteTask, taskToDelete, fetchTasks]);

  const openNotesModal = useCallback((task) => {
    setNotesTaskId(task.id);
    setNotesModalOpen(true);
  }, []);

  const closeNotesModal = useCallback(() => {
    setNotesModalOpen(false);
    setNotesTaskId(null);
  }, []);

  const handleAddNote = useCallback(
    (text) => {
      addAnnotation(notesTaskId, text).then((added) => {
        if (added) fetchTasks();
      });
    },
    [addAnnotation, notesTaskId, fetchTasks],
  );

  const notesTask = tasks.find((task) => task.id === notesTaskId) || null;

  // Day view: tasks without a date and the selected day's tasks are always shown; tasks from
  // days before the selected one only stay visible while not finished; later tasks stay hidden
  const visibleTasks = useMemo(
    () =>
      tasks.filter((task) => {
        if (!task.executionDate) return true;
        if (task.executionDate.isSame(selectedDate, 'day')) return true;
        if (task.executionDate.isBefore(selectedDate, 'day')) return task.columnId !== 'DONE';
        return false;
      }),
    [tasks, selectedDate],
  );

  const tasksByColumn = useMemo(
    () =>
      columns.map((column) =>
        visibleTasks.filter((task) => task.columnId === column.id).sort(sortColumnTasks),
      ),
    [visibleTasks],
  );

  const isToday = selectedDate.isSame(dayjs(), 'day');
  const formattedDate = selectedDate.locale('pt-br').format('dddd, DD/MM/YYYY');
  const shortFormattedDate = selectedDate.locale('pt-br').format('ddd, DD/MM');

  return {
    tasksByColumn,
    isLoading: isLoading || isRecurringLoading,
    selectedDate,
    isToday,
    formattedDate,
    shortFormattedDate,
    goToPreviousDay,
    goToNextDay,
    goToToday,

    taskModalOpen,
    editingTask,
    openNewTaskModal,
    openEditTaskModal,
    closeTaskModal,
    handleSaveTask,

    recurringTasks,
    recurringModalOpen,
    setRecurringModalOpen,
    handleRecurringAdd,
    handleRecurringUpdate,
    handleRecurringDelete,
    handleRecurringMove,

    handleAdvanceStage,
    handlePreviousStage,

    deleteModal,
    handleDeleteClick,
    closeDeleteModal,
    handleConfirmDelete,

    notesModalOpen,
    notesTask,
    openNotesModal,
    closeNotesModal,
    handleAddNote,

    showSuccessMessage: showSuccessMessage || showRecurringSuccessMessage,
    successMessage: showSuccessMessage ? successMessage : recurringSuccessMessage,
    closeSuccessMessage,
    showErrorMessage: showErrorMessage || showRecurringErrorMessage,
    errorMessage: showErrorMessage ? errorMessage : recurringErrorMessage,
    closeErrorMessage,
  };
}
